<template>
  <div>
    <div class="card">
      <div class="card-header py-2">
        <Spinner v-if="gsmLoading" margin_right="10px" height="20px" />
        <b-form-checkbox v-else class="display-inline marginright5" v-model="checkedAll" :disabled="!gsmLoaded"></b-form-checkbox>
        <span @click="gsmLoaded ? (checkedAll = !checkedAll) : null">GSM<br></span>
        <Spinner v-if="gsmUkeLoading" margin_right="10px" height="20px" />
        <b-form-checkbox v-else class="display-inline marginright5" v-model="checkedAllUKE" :disabled="!gsmUkeLoaded"></b-form-checkbox>
        <span @click="gsmUkeLoaded ? (checkedAllUKE = !checkedAllUKE) : null">GSM UKE</span>
      </div>
    </div>
  </div>
</template>

<style scoped>
:global(.gsm-pointer-cursor),
:global(.gsm-pointer-cursor *) {
  cursor: pointer !important;
}
</style>

<script lang="ts">
import BaseComponent from './Base.vue';
import { EventBus } from '@/ts/EventBus';
import { AlertStatus, DownloadStatus } from '@/ts/types';
import { defineComponent } from "vue";
import { DeckOverlay } from '@deck.gl-community/leaflet';
import { MapLasScatterplotLayer } from '@/ts/MapLasScatterplotLayer';

interface GsmResponsePlace {
  id: number;
  latitude: number;
  longitude: number;
  is_active: boolean;
}

interface GsmBinaryData {
  length: number;
  attributes: {
    getPosition: { value: Float32Array; size: number };
    getFillColor: { value: Uint8Array; size: number };
    getLineColor: { value: Uint8Array; size: number };
  };
}

interface GsmPreparedBinaryData {
  data: GsmBinaryData;
  results: GsmResponsePlace[];
}

interface GsmCardInstanceState {
  gsmResult: GsmResponsePlace[] | null;
  gsmUkeResult: GsmResponsePlace[] | null;
  gsmData: GsmBinaryData;
  gsmUkeData: GsmBinaryData;
  gsmLayerResults: GsmResponsePlace[];
  gsmUkeLayerResults: GsmResponsePlace[];
  deckOverlay: DeckOverlay | null;
  gsmLayer: MapLasScatterplotLayer | null;
  gsmUkeLayer: MapLasScatterplotLayer | null;
  mapMouseMoveHandler: ((event: L.LeafletMouseEvent) => void) | null;
  mapMouseOutHandler: (() => void) | null;
  mapClickHandler: ((event: L.LeafletMouseEvent) => void) | null;
}

function createEmptyBinaryData(): GsmBinaryData {
  return {
    length: 0,
    attributes: {
      getPosition: { value: new Float32Array(0), size: 2 },
      getFillColor: { value: new Uint8Array(0), size: 4 },
      getLineColor: { value: new Uint8Array(0), size: 4 },
    },
  };
}

declare module 'vue' {
  interface ComponentCustomProperties extends GsmCardInstanceState {}
}

export default defineComponent({
  extends: BaseComponent,
  data() {
    return {
      checkedAll: false,
      checkedAllUKE: false,
      gsmLoading: false,
      gsmUkeLoading: false,
      gsmLoaded: false,
      gsmUkeLoaded: false,
    };
  },
  created() {
    this.gsmData = createEmptyBinaryData();
    this.gsmUkeData = createEmptyBinaryData();
    this.gsmLayerResults = [];
    this.gsmUkeLayerResults = [];
    this.deckOverlay = null;
    this.gsmLayer = null;
    this.gsmUkeLayer = null;
    this.mapMouseMoveHandler = null;
    this.mapMouseOutHandler = null;
    this.mapClickHandler = null;
    EventBus.$on('downloadExternalData', () => this.downloadPlacesGSMandGSMUKE());
    EventBus.$on('selectedAreaChanged', () => this.onSelectedAreaChanged());
  },
  beforeUnmount() {
    if (window.mainmap && this.mapMouseMoveHandler) {
      window.mainmap.off('mousemove', this.mapMouseMoveHandler);
      this.mapMouseMoveHandler = null;
    }
    if (window.mainmap && this.mapMouseOutHandler) {
      window.mainmap.off('mouseout', this.mapMouseOutHandler);
      this.mapMouseOutHandler = null;
    }
    if (window.mainmap) {
      window.mainmap.getContainer().classList.remove('gsm-pointer-cursor');
    }
    if (window.mainmap && this.mapClickHandler) {
      window.mainmap.off('click', this.mapClickHandler);
      this.mapClickHandler = null;
    }
    if (this.deckOverlay && window.mainmap) {
      window.mainmap.removeLayer(this.deckOverlay);
      this.deckOverlay = null;
    }
  },
  methods: {
    isPointInSelectedArea(lat: number, lon: number) {
      // @ts-ignore
      const areaKey = window.selectedArea;
      if (!areaKey) return true;
      // @ts-ignore
      const area = window.areas[areaKey];
      if (!area || !area[0] || !area[1]) return true;
      const minLat = Math.min(area[0][0], area[1][0]);
      const maxLat = Math.max(area[0][0], area[1][0]);
      const minLon = Math.min(area[0][1], area[1][1]);
      const maxLon = Math.max(area[0][1], area[1][1]);
      if (Number.isNaN(lat) || Number.isNaN(lon)) return false;
      return lat >= minLat && lat <= maxLat && lon >= minLon && lon <= maxLon;
    },
    createBinaryData(results: GsmResponsePlace[], activeColor: number[], inactiveColor: number[]): GsmPreparedBinaryData {
      const visibleResults = results.filter((place) => this.isPointInSelectedArea(place.latitude, place.longitude));
      const positions = new Float32Array(visibleResults.length * 2);
      const fillColors = new Uint8Array(visibleResults.length * 4);
      const lineColors = new Uint8Array(visibleResults.length * 4);

      visibleResults.forEach((place, index) => {
        positions[index * 2] = place.longitude;
        positions[index * 2 + 1] = place.latitude;
        const color = place.is_active ? activeColor : inactiveColor;
        fillColors.set([color[0], color[1], color[2], 51], index * 4);
        lineColors.set(color, index * 4);
      });

      return {
        results: visibleResults,
        data: {
          length: visibleResults.length,
          attributes: {
            getPosition: { value: positions, size: 2 },
            getFillColor: { value: fillColors, size: 4 },
            getLineColor: { value: lineColors, size: 4 },
          },
        },
      };
    },
    ensureDeckOverlay() {
      if (!window.mainmap) return;
      if (!this.deckOverlay) {
        this.deckOverlay = new DeckOverlay({ layers: [] });
        window.mainmap.addLayer(this.deckOverlay);
      }
      if (!this.mapMouseMoveHandler) {
        this.mapMouseMoveHandler = (event: L.LeafletMouseEvent) => this.onMapMouseMove(event);
        window.mainmap.on('mousemove', this.mapMouseMoveHandler);
      }
      if (!this.mapMouseOutHandler) {
        this.mapMouseOutHandler = () => window.mainmap.getContainer().classList.remove('gsm-pointer-cursor');
        window.mainmap.on('mouseout', this.mapMouseOutHandler);
      }
      if (!this.mapClickHandler) {
        this.mapClickHandler = (event: L.LeafletMouseEvent) => this.onMapClick(event);
        window.mainmap.on('click', this.mapClickHandler);
      }
    },
    onMapMouseMove(event: L.LeafletMouseEvent) {
      const { lat, lng } = event.latlng;
      const hasGsmPoint = this.checkedAll && this.gsmLayer !== null && this.gsmLayer.pick(lat, lng) !== null;
      const hasGsmUkePoint = this.checkedAllUKE && this.gsmUkeLayer !== null && this.gsmUkeLayer.pick(lat, lng) !== null;
      window.mainmap.getContainer().classList.toggle('gsm-pointer-cursor', hasGsmPoint || hasGsmUkePoint);
    },
    onMapClick(event: L.LeafletMouseEvent) {
      const { lat, lng } = event.latlng;
      let place: GsmResponsePlace | undefined;

      if (this.checkedAll && this.gsmLayer) {
        const index = this.gsmLayer.pick(lat, lng);
        if (index !== null) place = this.gsmLayerResults[index];
      }
      if (!place && this.checkedAllUKE && this.gsmUkeLayer) {
        const index = this.gsmUkeLayer.pick(lat, lng);
        if (index !== null) place = this.gsmUkeLayerResults[index];
      }
      if (!place) return;

      const coords = `${place.latitude},${place.longitude}`;
      const mapLink = `https://maps.google.com/maps?q=${coords}`;
      const popupContent = window.isDesktop
        ? `${place.id} <b>${place.id}</b>&nbsp;<a href="${mapLink}" target="_blank">${place.latitude}, ${place.longitude}</a><span style="float: right; margin-right: 16px;"><br></span><br><br><img style="height: 5px; width: 5px;" src="${this.icons.blankWhite}">`
        : `<b>${place.id}</b>&nbsp;<a href="${mapLink}" target="_blank"><img style="height: 12px; cursor: pointer" src="${this.icons.locationBlack}"></a>`;
      window.mainmap.openPopup(popupContent, [place.latitude, place.longitude]);
    },
    updateGsmLayers() {
      this.ensureDeckOverlay();
      if (!this.deckOverlay) return;

      const layers = [];
      if (this.gsmLayer) {
        this.gsmLayer.setVisible(this.checkedAll);
        layers.push(this.gsmLayer.getLayer());
      }
      if (this.gsmUkeLayer) {
        this.gsmUkeLayer.setVisible(this.checkedAllUKE);
        layers.push(this.gsmUkeLayer.getLayer());
      }
      this.deckOverlay.setProps({ layers });
    },
    onSelectedAreaChanged() {
      if (this.checkedAll) {
        this.createGsmLayer();
      } else {
        this.gsmLayer = null;
      }
      if (this.checkedAllUKE) {
        this.createGsmUkeLayer();
      } else {
        this.gsmUkeLayer = null;
      }
      if (this.checkedAll || this.checkedAllUKE) {
        this.updateGsmLayers();
      }
    },
    onGsmCheckboxChanged() {
      if (this.checkedAll && !this.gsmLayer) {
        this.createGsmLayer();
      }
      this.updateGsmLayers();
    },
    onGsmUkeCheckboxChanged() {
      if (this.checkedAllUKE && !this.gsmUkeLayer) {
        this.createGsmUkeLayer();
      }
      this.updateGsmLayers();
    },
    createGsmLayer() {
      if (!this.gsmResult) return;
      const preparedData = this.createBinaryData(this.gsmResult, [255, 0, 255, 255], [255, 100, 255, 255]);
      this.gsmData = preparedData.data;
      this.gsmLayerResults = preparedData.results;
      this.gsmLayer = new MapLasScatterplotLayer({
        id: 'gsm-points',
        data: this.gsmData,
        positionFormat: 'XY',
        _normalize: false,
        getRadius: 8,
        radiusUnits: 'pixels',
        stroked: true,
        getLineWidth: 2,
        lineWidthUnits: 'pixels',
        visible: this.checkedAll,
        pickable: false,
      });
    },
    createGsmUkeLayer() {
      if (!this.gsmUkeResult) return;
      const preparedData = this.createBinaryData(this.gsmUkeResult, [128, 0, 128, 255], [156, 80, 156, 255]);
      this.gsmUkeData = preparedData.data;
      this.gsmUkeLayerResults = preparedData.results;
      this.gsmUkeLayer = new MapLasScatterplotLayer({
        id: 'gsmuke-points',
        data: this.gsmUkeData,
        positionFormat: 'XY',
        _normalize: false,
        getRadius: 8,
        radiusUnits: 'pixels',
        stroked: true,
        getLineWidth: 2,
        lineWidthUnits: 'pixels',
        visible: this.checkedAllUKE,
        pickable: false,
      });
    },
    downloadPlacesGSMandGSMUKE() {
      this.gsmLoading = true;
      let endPoint = window.apiHost + 'api/stringfield/gsm/';
      this.downloadUrlWithCache(endPoint, 'gsm', 'gsm.txt', null, true).then((response) => {
        this.gsmResult = response[1].results;
        this.gsmLoaded = true;
        if (response[0] == DownloadStatus.cache_fresh) {
          this.createAlert(AlertStatus.success, this.$t('placesLoaded', [response[1].results.length]).toString(), 2000);
        }
        if (response[0] == DownloadStatus.api_fresh) {
          this.createAlert(AlertStatus.success, this.$t('placesDownloaded', [response[1].results.length]).toString(), 2000);
        }
        if (response[0] == DownloadStatus.cache_last) {
          this.createAlert(AlertStatus.warning, this.$t('placesLoaded', [response[1].results.length]).toString(), 2000);
        }
      }).catch((error) => {
        this.createAlert(AlertStatus.danger, this.$t('placesError').toString(), 2000);
      }).finally(() => {
        this.gsmLoading = false;
      });
      this.gsmUkeLoading = true;
      endPoint = window.apiHost + 'api/stringfield/gsmuke/';
      this.downloadUrlWithCache(endPoint, 'gsmuke', 'gsmuke.txt', null, true).then((response) => {
        this.gsmUkeResult = response[1].results;
        this.gsmUkeLoaded = true;
        if (response[0] == DownloadStatus.cache_fresh) {
          this.createAlert(AlertStatus.success, this.$t('placesLoaded', [response[1].results.length]).toString(), 2000);
        }
        if (response[0] == DownloadStatus.api_fresh) {
          this.createAlert(AlertStatus.success, this.$t('placesDownloaded', [response[1].results.length]).toString(), 2000);
        }
        if (response[0] == DownloadStatus.cache_last) {
          this.createAlert(AlertStatus.warning, this.$t('placesLoaded', [response[1].results.length]).toString(), 2000);
        }
      }).catch((error) => {
        this.createAlert(AlertStatus.danger, this.$t('placesError').toString(), 2000);
      }).finally(() => {
        this.gsmUkeLoading = false;
      });
    }
  },
  watch: {
    checkedAll() {
      this.onGsmCheckboxChanged();
    },
    checkedAllUKE() {
      this.onGsmUkeCheckboxChanged();
    },
  }
})
</script>